/**
 * Account lifecycle on the chambers side of the web app — ONE place that decides how an account's
 * status is named and which lifecycle action a person sees on someone else's account.
 *
 * Vocabulary (CLAUDE.md #25): Active → Deactivated → Reactivated. The server's `inactive` status is a
 * DEACTIVATED account. Account deletion does not exist.
 *
 * Authority is the SERVER's: users.service.ts `assertCanManageMemberType` (admin manages anyone; a
 * senior clerk manages the employed team; nobody else manages accounts), plus "never your own
 * account". This mirrors it only so a person is never shown a button the server would refuse — and,
 * the other way round, so an authorised person always HAS a button for a deactivated account.
 */

export type AccountStatus = 'active' | 'invited' | 'suspended' | 'inactive';

export const ACCOUNT_STATUS_LABEL: Record<AccountStatus, string> = {
  active: 'Active',
  invited: 'Invited',
  suspended: 'Suspended',
  inactive: 'Deactivated',
};

export function accountStatusLabel(status: string): string {
  return ACCOUNT_STATUS_LABEL[status as AccountStatus] ?? status;
}

const BARRISTER_MEMBER_TYPES = new Set(['silk', 'junior', 'pupil', 'door_tenant']);
const OPERATIONAL_STAFF_TYPES = new Set(['senior_clerk', 'junior_clerk', 'staff']);

/** Mirror of the server's assertCanManageMemberType — keep the two in step. */
export function canManageMemberAccount(actorType: string, targetType: string): boolean {
  if (actorType === 'admin') return true;
  if (actorType === 'senior_clerk') return OPERATIONAL_STAFF_TYPES.has(targetType) && !BARRISTER_MEMBER_TYPES.has(targetType);
  return false;
}

export type MemberAccountAction =
  | { kind: 'reactivate'; nextStatus: 'active' }
  | { kind: 'suspend'; nextStatus: 'suspended' }
  | null;

/**
 * The lifecycle action `viewer` may take on `member`'s account, or null for none. A suspended or a
 * DEACTIVATED account offers Reactivate (one action → Active); an active or invited account offers
 * Suspend. Never on your own account, never on a solicitor, never beyond the viewer's authority.
 */
export function memberAccountAction(
  viewer: { id: string; memberType: string } | null | undefined,
  member: { id: string; memberType: string; status: string },
): MemberAccountAction {
  if (!viewer || viewer.id === member.id) return null;
  if (member.memberType === 'solicitor') return null;
  if (!canManageMemberAccount(viewer.memberType, member.memberType)) return null;
  if (member.status === 'suspended' || member.status === 'inactive') return { kind: 'reactivate', nextStatus: 'active' };
  if (member.status === 'active' || member.status === 'invited') return { kind: 'suspend', nextStatus: 'suspended' };
  return null;
}
